import React, { useEffect } from 'react';
import Intl from '@fx-ui/intl';
import { Button } from '@fx-ui/fine-design';
import {
    assign,
    compact,
    every,
    filter,
    get,
    getAvailablePersonaData,
    getPersonaCollectAvailableFields,
    getQueryRouter,
    getUrlParameter,
    isEmpty,
    isMobileUA,
    isNil,
    isNumber,
    redirect,
    size,
    tracker,
    trim
} from '~/utils';
import { SITE_ROUTER, SuiteId, TRACKER, UGC_MAX_LENGTH, URL_PARAMETER } from '~/constants';
import { PersonaCustomComponent, PersonaInfo, PersonaLabel } from '~/interfaces';
import PersonaCollect from '../persona-collect';
import CorpName from '../persona-collect/components/corp-name';
import Requirement from '../persona-collect/components/requirement';
import Industry from '../persona-collect/components/industry';
import Favorite from '../persona-collect/components/favorite';
import CustomComponents from '../persona-collect/components/custom-components';
import { createCorpInRegister } from '~/request/corp';
import { updatePersonaInfo } from '~/request/persona';
import classNames from 'classnames';

interface CorpPersonaCollectProps {
    personaInfo?: PersonaInfo;
    personaLabel?: PersonaLabel;
    suiteId?: SuiteId;
    setPersonaInfo: (action: (prevState: PersonaInfo) => PersonaInfo) => void;
    onComplete: () => void;
}

const { CORP_NAME } = UGC_MAX_LENGTH.USER;

/** 获取创建团队页支持的自定义画像字段，保持配置顺序。 */
const getCorpPersonaCustomComponents = (personaLabel?: PersonaLabel) => {
    return filter(
        personaLabel?.custom_components,
        (component) => component.scope === 'tenant' && component.enabled !== false
    );
};

/** 所有展示的自定义画像字段均必填。 */
const isPersonaCustomDataComplete = (
    components: PersonaCustomComponent[],
    data: PersonaInfo['suiteCustomData'] = {}
) => {
    if (isEmpty(components)) {
        return true;
    }
    return every(components, (component) => {
        const value = data[component.key];
        return !isNil(value) && !isEmpty(value);
    });
};

const CorpPersonaCollect: React.FC<CorpPersonaCollectProps> = (props) => {
    const { onComplete, personaInfo, personaLabel, suiteId, setPersonaInfo } = props;

    const { industry, requirement, favorites, companyDetail, corpName, suiteCustomData } =
        personaInfo || {};
    const customComponents = getCorpPersonaCustomComponents(personaLabel);

    useEffect(() => {
        tracker(TRACKER.CREATE_CORP_VISIT);
    }, []);

    const isDisabled = () => {
        if (!personaLabel) {
            return true;
        }
        const availableFields = getPersonaCollectAvailableFields(personaLabel, industry?.value);
        return (
            isEmpty(corpName) ||
            size(trim(corpName)) > CORP_NAME ||
            (availableFields.requirement && isEmpty(requirement?.value)) ||
            (availableFields.industry && isEmpty(industry?.value)) ||
            (availableFields.favorites && isEmpty(favorites)) ||
            !isPersonaCustomDataComplete(customComponents, suiteCustomData)
        );
    };

    const handleClick = () => {
        const availableFields = getPersonaCollectAvailableFields(personaLabel, industry?.value);
        const trackParams: Record<string, string> = {};
        if (availableFields.industry) {
            assign(trackParams, {
                industry: industry!.value
            });
        }
        if (availableFields.favorites) {
            assign(trackParams, {
                favorite: favorites?.join() || ''
            });
        }
        if (availableFields.requirement) {
            assign(trackParams, {
                requirement: requirement!.value
            });
        }
        setPersonaInfo((prev) => {
            return {
                ...prev,
                hasCollectedCorpInfo: true
            };
        });
        const trimCorpName = trim(corpName);
        return createCorpInRegister({
            corpName: trimCorpName,
            trackParams,
            suiteId
        })
            .then((data) => {
                const tenantId = get(data, 'tenant.id');
                setPersonaInfo((prev) => ({
                    ...prev,
                    tenantId
                }));
                const personaData = {
                    company: trimCorpName,
                    company_detail: companyDetail,
                    ...getAvailablePersonaData(availableFields, {
                        requirement: requirement?.value,
                        industry: industry?.value,
                        favorites
                    }),
                    ...(isEmpty(customComponents) ? {} : { suite_custom_data: suiteCustomData })
                };
                return updatePersonaInfo({
                    data: personaData,
                    tenantId,
                    suiteId
                });
            })
            .finally(() => {
                onComplete();
            });
    };

    return (
        <PersonaCollect
            className={classNames('fx-corp-persona-collect', {
                'is-mobile': isMobileUA()
            })}
            title={Intl.get('persona.header.create')}
            subTitle={Intl.get('persona.header.detail.create')}
            personaInfo={personaInfo}
            personaLabel={personaLabel}
            setPersonaInfo={setPersonaInfo}
        >
            <CorpName />
            <Requirement />
            <Industry />
            <Favorite />
            <CustomComponents
                components={customComponents}
                data={suiteCustomData}
                onChange={(key, value) => {
                    setPersonaInfo((prev) => ({
                        ...prev,
                        suiteCustomData: { ...prev.suiteCustomData, [key]: value }
                    }));
                }}
            />
            <Button
                className="next-btn"
                type="primary"
                size="large"
                block
                disabled={isDisabled()}
                loadingWhenClick
                onClick={handleClick}
            >
                {Intl.get('nextStep')}
            </Button>
            <div className="join-corp-tip">
                {Intl.getHTML('corp.join.tip', {
                    SpanHighLight: (s) => (
                        <span
                            className="join-corp-btn"
                            onClick={() => {
                                const redirect_uri = getUrlParameter(URL_PARAMETER.REDIRECT_URI);
                                const suite_id = suiteId ?? window.platform_suite_id;
                                const suite_metadata = window.platform_suite_metadata;
                                const url = getQueryRouter(
                                    compact([
                                        redirect_uri && {
                                            key: URL_PARAMETER.REDIRECT_URI,
                                            value: encodeURIComponent(redirect_uri)
                                        },
                                        isNumber(suite_id) && {
                                            key: URL_PARAMETER.SUITE_ID,
                                            value: suite_id
                                        },
                                        suite_metadata && {
                                            key: URL_PARAMETER.SUITE_METADATA,
                                            value: suite_metadata
                                        }
                                    ]),
                                    SITE_ROUTER.WELCOME
                                );
                                redirect(url);
                            }}
                        >
                            {s}
                        </span>
                    )
                })}
            </div>
        </PersonaCollect>
    );
};

export default CorpPersonaCollect;
